import * as React from "react";
import {
  sanitizeNumericText,
  parseDecimal,
  normalizeForEmit,
  stripFractional,
  applyNumericBounds,
} from "@brewbase/lib";
import { Input } from "./input";

type InputProps = React.ComponentProps<typeof Input>;

/**
 * The emitted/accepted value type, keyed off the `precise` flag:
 * default (`precise` unset/false) is `number`; `precise` is `string`.
 */
type NumericValue<P extends boolean> = P extends true ? string : number;

/** Validation rules. `min`/`max`/`step` are intercepted as real numbers, not HTML attrs. */
interface NumericValidationProps {
  /**
   * Restrict to whole numbers. The decimal separator can't be typed
   * (keystroke-filtered) and the committed value is rounded to an integer on
   * blur. Sugar for `step={1}`.
   */
  integer?: boolean;
  /** Lower bound. The committed value is clamped to `>= min` on blur. */
  min?: number;
  /** Upper bound. The committed value is clamped to `<= max` on blur. */
  max?: number;
  /**
   * Snap the committed value to the nearest multiple of `step` (anchored at
   * `min ?? 0`) on blur. Enforced at commit time only — never mid-typing.
   */
  step?: number;
}

interface NumericInputProps<P extends boolean = false>
  extends Omit<
      InputProps,
      "value" | "onChange" | "type" | "min" | "max" | "step" | "ref"
    >,
    NumericValidationProps {
  /**
   * When true, the value is a raw decimal STRING, preserved losslessly
   * (including trailing zeros) for pg NUMERIC columns. When false/unset,
   * the value is a JS `number` (the default — every existing caller).
   */
  precise?: P;
  value: NumericValue<P> | null | undefined;
  onValueChange: (value: NumericValue<P> | null) => void;
}

/** Widened props the implementation actually runs on. */
type NumericInputImplProps = Omit<
  InputProps,
  "value" | "onChange" | "type" | "min" | "max" | "step" | "ref"
> &
  NumericValidationProps & {
    precise?: boolean;
    value: string | number | null | undefined;
    onValueChange: (value: string | number | null) => void;
  };

function valueToText(value: string | number | null | undefined): string {
  return value == null ? "" : String(value);
}

/**
 * A numeric input backed by the @brewbase/lib numeric-text helpers — the
 * single source of truth for decimal handling. Holds local string state to
 * preserve intermediate text ("1.", "-", "1.500") while typing, filters
 * disallowed characters at the keystroke (junk never appears, never resets
 * to 0), normalizes ',' → '.', and reports a genuine null for blank.
 *
 * Validation rules (`integer`, `min`, `max`, `step`) are COMMIT-time only:
 * applied on blur, never per keystroke, so the user can freely type a fragment
 * like "1" on the way to "12" with `min={10}` set.
 */
const NumericInputImpl = React.forwardRef<
  HTMLInputElement,
  NumericInputImplProps
>(function NumericInput(
  {
    precise = false,
    integer = false,
    min,
    max,
    step,
    value,
    onValueChange,
    onBlur,
    ...props
  },
  ref,
) {
  const [text, setText] = React.useState(() => valueToText(value));
  const [lastValue, setLastValue] = React.useState(value);

  // `integer` is sugar for a step of 1 (snap to whole numbers on blur).
  const snapStep = step ?? (integer ? 1 : undefined);
  const bounds = { min, max, step: snapStep };
  const hasBounds = min != null || max != null || snapStep != null;

  // Effect-free external sync. When the controlled prop changes to a value
  // our local text does not already represent, adopt it. The emit-equality
  // guard preserves mid-edit fragments when the parent echoes the same value.
  if (value !== lastValue) {
    setLastValue(value);
    const incoming = value ?? null;
    const emitted = precise ? normalizeForEmit(text) : parseDecimal(text);
    const matches = precise
      ? emitted === incoming
      : emitted === (incoming == null ? null : Number(incoming));
    if (!matches) setText(valueToText(value));
  }

  const emit = (t: string) =>
    onValueChange(precise ? normalizeForEmit(t) : parseDecimal(t));

  return (
    <Input
      ref={ref}
      type="text"
      inputMode={integer ? "numeric" : "decimal"}
      value={text}
      onChange={(e) => {
        const el = e.target;
        const raw = el.value;
        const caret = el.selectionStart ?? raw.length;
        let sanitized = sanitizeNumericText(raw);
        if (integer) sanitized = stripFractional(sanitized);
        setText(sanitized);
        emit(sanitized);
        // Filtering only removes characters; restore the caret by the
        // length delta so mid-string edits don't jump to the end.
        if (sanitized !== raw) {
          const removed = raw.length - sanitized.length;
          const next = Math.max(0, Math.min(sanitized.length, caret - removed));
          requestAnimationFrame(() => {
            try {
              el.setSelectionRange(next, next);
            } catch {
              /* selection not supported — ignore */
            }
          });
        }
      }}
      onBlur={(e) => {
        // Canonicalize the display on blur (strip spaces, "1." → "1") and
        // apply the validation rules. Clamp/snap only rewrites the text when
        // it actually changes the value, so in-range entries keep their exact
        // form (including trailing zeros in precise mode).
        const parsed = parseDecimal(text);
        if (parsed == null) {
          if (text !== "") setText("");
        } else {
          const bounded = hasBounds ? applyNumericBounds(parsed, bounds) : parsed;
          if (bounded !== parsed) {
            const s = String(bounded);
            setText(s);
            onValueChange(precise ? s : bounded);
          } else if (precise) {
            setText(normalizeForEmit(text) ?? "");
          } else {
            setText(String(parsed));
          }
        }
        onBlur?.(e);
      }}
      {...props}
    />
  );
});
NumericInputImpl.displayName = "NumericInput";

// Public generic façade: ties the value's in-type to its out-type via `precise`
// while keeping a real forwarded `ref` to the underlying <input>. One boundary
// cast — the widened impl soundly implements every instantiation.
const NumericInput = NumericInputImpl as unknown as <P extends boolean = false>(
  props: NumericInputProps<P> & React.RefAttributes<HTMLInputElement>,
) => React.ReactElement;

export { NumericInput };
export type { NumericInputProps };
